/**
 * Push Notification Service
 * 
 * Handles Web Push API integration with service worker for mobile-friendly
 * push notifications with sound and vibration support.
 * 
 * Features:
 * - Service worker registration
 * - Push subscription management
 * - Local notification display with sound
 * - Vibration patterns for different priorities
 * - Backend subscription sync
 */

// VAPID public key - In production, this would come from environment
// For now, we'll use local notifications which don't require VAPID
const VAPID_PUBLIC_KEY = null;

/**
 * Why push can't be turned on here, or null if it can be requested.
 * iPhone/iPad only allow web push from an app added to the Home Screen (iOS 16.4+).
 */
export const pushBlockReason = () => {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
  if (isIOS && !standalone) return 'ios_install';
  if (!('Notification' in window) || !('PushManager' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return null;
};

export const pushHelpMessage = (reason) => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '');
  if (reason === 'ios_install') {
    return 'On iPhone, tap the Share button, then "Add to Home Screen". Open Drayly from the new icon and tap Enable again.';
  }
  if (reason === 'unsupported') return 'This browser does not support notifications. Use Chrome or Safari (from the Home Screen icon on iPhone).';
  return isIOS
    ? 'Notifications are turned off for Drayly. Open iPhone Settings → Notifications → Drayly and turn on Allow Notifications.'
    : 'Notifications are blocked for this site. Tap the lock icon next to the address (or Chrome ⋮ → Settings → Site settings → Notifications), allow notifications for this site, then reload.';
};

/**
 * Check if push notifications are supported
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
};

/**
 * Check if notifications are permitted
 */
export const isNotificationPermitted = () => {
  return Notification.permission === 'granted';
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported');
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission === 'denied') {
    console.warn('Notifications are blocked by user');
    return false;
  }
  
  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

/**
 * Register service worker for push notifications
 */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    
    console.log('[Push] Service worker registered:', registration.scope);
    
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    
    return registration;
  } catch (error) {
    console.error('[Push] Service worker registration failed:', error);
    return null;
  }
};

/**
 * Get current push subscription
 */
export const getSubscription = async () => {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
};

/**
 * Subscribe to push notifications
 */
export const subscribeToPush = async (apiEndpoint = null) => {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  // Request permission first
  const permitted = await requestNotificationPermission();
  if (!permitted) {
    throw new Error('Notification permission denied');
  }
  
  // Get service worker registration
  const registration = await navigator.serviceWorker.ready;
  
  // Check existing subscription
  let subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    console.log('[Push] Existing subscription found');
    return subscription;
  }
  
  // For full push notification support, you need a VAPID key
  // For now, we'll rely on local notifications via the app
  if (VAPID_PUBLIC_KEY) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
    
    // Send subscription to backend
    if (apiEndpoint) {
      await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription)
      });
    }
    
    console.log('[Push] New subscription created');
  }
  
  return subscription;
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async () => {
  const subscription = await getSubscription();
  
  if (subscription) {
    await subscription.unsubscribe();
    console.log('[Push] Unsubscribed');
    return true;
  }
  
  return false;
};

/**
 * Send a local notification with sound and vibration
 * This works even without push subscription (for in-app notifications)
 */
export const sendLocalNotification = async (title, options = {}) => {
  const permitted = await requestNotificationPermission();
  if (!permitted) {
    console.warn('Notifications not permitted');
    return null;
  }
  
  // Get service worker registration
  const registration = await navigator.serviceWorker.ready;
  
  // Default options with sound and vibration
  const defaultOptions = {
    body: '',
    icon: '/icons/driver-icon.svg',
    badge: '/icons/driver-icon.svg',
    tag: `local-${Date.now()}`,
    renotify: true,
    requireInteraction: false,
    silent: false,  // Enable sound
    vibrate: [100, 50, 100],  // Default vibration pattern
    data: {}
  };
  
  // Priority-based vibration patterns
  if (options.priority === 'high') {
    defaultOptions.vibrate = [200, 100, 200, 100, 200];
    defaultOptions.requireInteraction = true;
  } else if (options.priority === 'critical') {
    defaultOptions.vibrate = [300, 100, 300, 100, 300, 100, 300];
    defaultOptions.requireInteraction = true;
  }
  
  const mergedOptions = { ...defaultOptions, ...options };
  
  // Play custom sound for browsers that support Web Audio
  if (!mergedOptions.silent && options.playCustomSound !== false) {
    playNotificationSound(options.priority || 'normal');
  }
  
  // Trigger vibration on mobile
  if (mergedOptions.vibrate && 'vibrate' in navigator) {
    navigator.vibrate(mergedOptions.vibrate);
  }
  
  // Show notification via service worker
  await registration.showNotification(title, mergedOptions);
  
  return true;
};

/**
 * Play notification sound using Web Audio API
 */
export const playNotificationSound = (priority = 'normal') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Different tones based on priority
    if (priority === 'critical') {
      // Urgent alarm-like sound
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(660, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(660, audioContext.currentTime + 0.3);
      gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } else if (priority === 'high') {
      // Two-tone alert
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1174.66, audioContext.currentTime + 0.15);
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.3);
      gainNode.gain.setValueAtTime(0.35, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } else {
      // Simple pleasant notification sound
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(587.33, audioContext.currentTime); // D5
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.1); // A5
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.25);
    }
    
    return true;
  } catch (error) {
    console.warn('Could not play notification sound:', error);
    return false;
  }
};

/**
 * Trigger device vibration
 */
export const vibrate = (pattern = [100, 50, 100]) => {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
    return true;
  }
  return false;
};

/**
 * Notification types for the app
 */
export const NotificationTypes = {
  DISPATCH: 'dispatch',
  MESSAGE: 'message',
  CLOCKIN_REMINDER: 'clockin_reminder',
  TERMINAL_ALERT: 'terminal_alert',
  AVAILABILITY_UPDATE: 'availability_update',
  COMMUNITY_REPORT: 'community_report',
  GENERAL: 'general'
};

/**
 * Priority levels
 */
export const NotificationPriority = {
  NORMAL: 'normal',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Helper: Convert VAPID key to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

/**
 * Send notification helper with default handling
 */
export const notify = async ({
  title,
  body,
  type = NotificationTypes.GENERAL,
  priority = NotificationPriority.NORMAL,
  data = {},
  actions = [],
  sound = true,
  icon = '/icons/driver-icon.svg'
}) => {
  return sendLocalNotification(title, {
    body,
    icon,
    priority,
    silent: !sound,
    data: { type, ...data },
    actions
  });
};

export default {
  isPushSupported,
  isNotificationPermitted,
  requestNotificationPermission,
  registerServiceWorker,
  getSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendLocalNotification,
  playNotificationSound,
  vibrate,
  notify,
  NotificationTypes,
  NotificationPriority
};
